import {
  LoadingOutlined,
  ReloadOutlined,
  SettingOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Flex, Popover, Spin, Tooltip } from 'antd';
import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import EmbeddedAppFrame from '@/common/components/EmbeddedAppFrame';
import { useAppContext } from '@/common/hooks/AppStateProvider';
import { useOrganizationPath } from '@/common/hooks/use-organization-path';
import { getHarnessInstance, startHarnessInstance } from '../services/harness';
import {
  canManageHarness,
  canStartHarness,
  getHarnessFrameSrc,
  getHarnessWebUrl,
  isHarnessBusy,
} from '../utils/harness';
import HarnessStatusIndicator from './HarnessStatusIndicator';

const HarnessInstance = ({ fit, src }) => {
  const { organizationId, resolveOrganizationPath } = useOrganizationPath();
  const { session } = useAppContext();
  const queryClient = useQueryClient();
  const attemptedStart = useRef(false);
  const [frameSrc, setFrameSrc] = useState();
  const queryKey = ['harness', organizationId, session.user.id, 'instance'];
  const instance = useQuery({
    queryKey,
    queryFn: () => getHarnessInstance({ organizationId }),
    retry: false,
    refetchInterval: (query) => (query.state.error ? false : 5000),
  });
  const start = useMutation({
    mutationFn: () => startHarnessInstance({ organizationId }),
    retry: false,
    onError: () => queryClient.invalidateQueries({ queryKey }),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKey, data);
      queryClient.invalidateQueries({ queryKey });
    },
  });
  const status = instance.data?.status;
  const busy = start.isPending || isHarnessBusy(status);
  const { mutate: startInstance } = start;
  const startable =
    !instance.data?.upgradeRetryRequired && canStartHarness(status, instance.data?.error);
  useEffect(() => {
    if (!instance.isError && !attemptedStart.current && startable) {
      attemptedStart.current = true;
      startInstance();
    }
  }, [instance.isError, startable, startInstance]);

  useEffect(() => {
    // Replaying a creation URL after recovery would start another task.
    setFrameSrc((currentSrc) =>
      getHarnessFrameSrc(currentSrc, status, src || getHarnessWebUrl(organizationId)),
    );
  }, [organizationId, src, status]);

  const retryAction = !instance.isError &&
    startable &&
    (frameSrc || start.isError || status === 'failed') && (
      <Tooltip title="重试启动">
        <Button
          aria-label="重试"
          type="primary"
          icon={<ReloadOutlined />}
          loading={busy}
          onClick={() => start.mutate()}
        >
          {frameSrc ? null : '重试'}
        </Button>
      </Tooltip>
    );
  const upgradeAlert = (instance.data?.upgradeRetryRequired ||
    instance.data?.error === 'HARNESS_UPGRADE_FAILED') && (
    <Alert
      type="warning"
      showIcon
      title="请联系组织管理员重试版本更新。"
      action={
        canManageHarness(session.role) && (
          <Button
            aria-label="管理升级"
            icon={<SettingOutlined />}
            href={resolveOrganizationPath('/settings/agents?tab=harness')}
          >
            管理升级
          </Button>
        )
      }
    />
  );

  return (
    <EmbeddedAppFrame
      title="智能助手"
      fit={fit}
      src={frameSrc}
      extra={
        <Flex align="center" gap="small">
          <HarnessStatusIndicator
            fetching={instance.isFetching}
            onRetry={() => instance.refetch()}
            queryError={instance.isError}
            status={status}
            version={instance.data?.tag}
          />
          {frameSrc && retryAction}
          {frameSrc && upgradeAlert && (
            <Popover content={upgradeAlert} placement="bottomRight" trigger="click">
              <Button aria-label="查看升级提示" icon={<WarningOutlined />} />
            </Popover>
          )}
        </Flex>
      }
    >
      {retryAction}
      {instance.isPending && <Spin />}
      {instance.isError && (
        <Alert
          type="warning"
          showIcon
          title="状态暂时无法确认"
          description="请通过状态提示重试查询。"
        />
      )}
      {start.isError && status !== 'running' && (
        <Alert type="error" showIcon title="智能助手准备失败" description="请重试。" />
      )}
      {upgradeAlert}
      {busy && (
        <Alert type="info" showIcon icon={<LoadingOutlined spin />} title="正在准备智能助手" />
      )}
    </EmbeddedAppFrame>
  );
};

HarnessInstance.propTypes = {
  fit: PropTypes.oneOf(['viewport', 'container']),
  src: PropTypes.string,
};

export default HarnessInstance;
